import useConveyanceTableViewModel from './ConveyanceTableViewModel'
import cn from 'classnames'
import { MAIN_TAB, TAB_TYPE_DEED, TAB_TYPE_LEASE } from '@constants'
import RunsheetLegalDescriptionModal from '@components/legaldescription/RunsheetLegalDescriptionModal'
import { Message } from '@components/shared/Message'
import { DocumentViewerModal } from '@components/collection/DocumentViewerV2'
import MergePartiesModal from '../MergePartiesModal/MergePartiesModal'
import { UpsertDocument } from '../../UpsertDocument'
import { FlowchartModal } from '@components/ChainFlowchart'
import * as actions from '@actions'
import ConveyanceDoc from '../ConveyanceDoc'
import './ConveyanceTable.css'
import CircleSpinner from '@components/shared/CircleSpinner'
import RunsheetTab from '../../Runsheet/RunsheetTab'
import { getRunsheetDocsByTab } from '../../Runsheet/Runsheet.lib'
import { toast } from 'react-toastify'
import ConveyanceTableHeader from './ConveyanceTableHeader'
import ConveyanceTableBody from './ConveyanceTableBody'
import { AssignTracts } from '../../AssignTracts'
import { UpsertTract } from '../../UpsertTract'

export const ConveyanceTable = ({
    chain,
    setChain,
    conveyanceType,
    runsheetDocs,
    activeTab,
    hasRecentFailure,
    hasMissingData,
    setHasRecentFailure,
    handleChain,
    exportChain,
    exportLoading,
    chainingBreadcrumb,
}) => {
    
    const vm = useConveyanceTableViewModel({
        chain,
        setChain,
        conveyanceType,
        runsheetDocs,
        activeTab,
    })
    
    if (hasMissingData)
        return (
            <div className="ConveyanceTable flex flex-col">
                {chainingBreadcrumb()}
                <div className="flex flex-col items-center">
                    <div className="text-2xl my-5 mt-10">There's missing data from documents in this Runsheet.
                        The chainer can't proceed.</div>
                </div>
            </div>
        )
    
    if (!chain)
        return (
            <div className="ConveyanceTable flex flex-col">
                {chainingBreadcrumb()}
                <div className="flex flex-col items-center">
                    <div className="text-2xl my-5 mt-10">{conveyanceType === TAB_TYPE_DEED
                        ? 'Title Chain'
                        : 'Leasehold Chain'} is loading...</div>
                    <CircleSpinner />
                </div>
            </div>
        )
    
    
    return (<>
        
        {chainingBreadcrumb()}
        
        <div className="ConveyanceTable px-4 pb-4">
            {hasRecentFailure &&
                <Message
                    message={{
                        text: 'Last Chaining failed. Try again to get latest results',
                        level: 'info',
                    }}
                    className="w-[500px]"
                    onDismiss={() => setHasRecentFailure(false)} />
            }
        </div>
        
        <div className="ConveyanceTable flex w-100 w-screen overflow-auto h-[calc(100vh-156px)]" ref={vm.titleRef}>
            
            <div className="flex flex-col sticky left-0 title-chain-sidebar">
                <table className={cn('bg-base-100 text-sm', {
                    'title-table': conveyanceType === TAB_TYPE_DEED,
                    'leasehold-table': conveyanceType === TAB_TYPE_LEASE,
                })}>
                    <ConveyanceTableHeader
                        chain={chain}
                        handleChain={handleChain}
                        chainHeaders={vm.chainHeaders}
                        getTooltipContent={vm.getTooltipContent}
                        documentsToProcess={vm.documentsToProcess}
                        setShowFlowchart={vm.setShowFlowchart}
                        getTractLink={vm.getTractLink}
                        getTotalConveyances={vm.getTotalConveyances}
                        exportChain={exportChain}
                        exportLoading={exportLoading}
                        handleDeleteTitleChain={vm.handleDeleteTitleChain}
                        setShowAssignTract={vm.setShowAssignTract} />
                    
                    <ConveyanceTableBody
                        parties={vm.parties}
                        getPartyMergeCount={vm.getPartyMergeCount}
                        setSelectedParty={vm.setSelectedParty}
                        chainHeaders={vm.chainHeaders}
                        sumConveyances={vm.sumConveyances} />
                </table>
            </div>
            
            <div className="flex">
                {vm.documents.map(doc =>
                    <ConveyanceDoc
                        key={doc.documentId}
                        conveyanceType={conveyanceType}
                        document={doc}
                        parties={vm.parties}
                        rsDocJoin={runsheetDocs}
                        setEditRsDocJoin={vm.setEditRsDocJoin}
                        showAdd={vm.showAdd}
                        showHideAdd={vm.setShowAdd} />)}
            </div>
        
        </div>
        
        <RunsheetTab runsheetId={vm.runsheetId} canEdit={false} shouldSelectTab={tabId => {
            
            if (!vm.remoteConfig.chainer?.validateMissingData) return true
            
            const tab = vm.tabs.find(t => t.id === tabId)
            const docs = getRunsheetDocsByTab(tab, vm.runsheet)
            
            // if important data is missing, then inform the user through a tooltip
            const hasMissingData = docs.some(r =>
                !r.value.instrumentDate || !r.value.grantors.length || !r.value.grantees.length)
            
            if (!tab) return false
            
            if (hasMissingData) {
                toast.warn('There\'s missing data from documents in this Runsheet Tab. The chainer can\'t proceed.')
            } else {
                if (tab.type === TAB_TYPE_DEED && conveyanceType === TAB_TYPE_LEASE) {
                    return vm.navigate(`/runsheet/${vm.runsheetId}/leasehold-chain?activeTab=${tabId}`)
                } else if (tab.type === TAB_TYPE_LEASE && conveyanceType === TAB_TYPE_DEED) {
                    return vm.navigate(`/runsheet/${vm.runsheetId}/title-breakdown?activeTab=${tabId}`)
                }
            }
            
            return !hasMissingData
        }} />
        
        <DocumentViewerModal />
        
        {vm.showAdd && (
            <UpsertDocument
                runsheetId={vm.runsheetId}
                showAdd={vm.showAdd}
                setShowAdd={vm.setShowAdd}
                editRsDocJoin={vm.editRsDocJoin}
                onSuccess={vm.updateDocData} />
        )}
        
        <RunsheetLegalDescriptionModal
            setShowAdd={vm.setShowAdd}
            setEditRsDocJoin={vm.setEditRsDocJoin} />
        
        <MergePartiesModal
            titleBreakdownId={chain.id}
            runsheetId={vm.runsheetId}
            selectedParty={vm.selectedParty}
            handleClose={async isSuccess => {
                vm.setSelectedParty(null)
                
                if (isSuccess) {
                    setChain(null)
                    const data = await Promise.all([actions.fetchTitleBreakdown(vm.runsheetId, activeTab),
                        actions.listUniqueParties(vm.runsheetId)])
                    
                    setChain(data[0])
                    setHasRecentFailure(data[0].hasRecentFailure)
                    
                }
            }
            } />
        
        <FlowchartModal
            open={vm.showFlowchart}
            modalHandler={() => vm.setShowFlowchart(false)}
            chain={chain} />
        
        {vm.showAssignTract && (
            <AssignTracts
                showAdd={vm.showAssignTract}
                showHideAdd={vm.setShowAssignTract}
                showCreateTract={vm.setShowCreateTract}
                isRunsheetTab={vm.activeTab && vm.activeTab !== MAIN_TAB}
                tractId={vm.tract?.id || ''}
                tabId={vm.activeTab}
                runsheetId={vm.runsheet.id}
                ownershipType={vm.ownershipType}
                setOwnershipType={vm.setOwnershipType} />
        )}
        {vm.showCreateTract && (
            <UpsertTract
                showAdd={vm.showCreateTract}
                showHideAdd={vm.setShowCreateTract}
                runsheetTabId={vm.activeTab}
                runsheetId={vm.runsheet.id}
                ownershipType={vm.ownershipType}
                shouldAssign={true} />
        )}
    
    </>)
    
} 
